import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { isLessThanBreakpoint } from 'dpl/util/grid';
import LitterParentsVennImages from 'dpl/breeder_dashboard/components/LitterParentsVennImages';
import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownToggle,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';
import Icon from 'dpl/common/components/Icon';
import Label from 'dpl/form/components/Label';
import useAssignableLitterData from 'dpl/breeder_dashboard/hooks/useAssignableLitterData';
import FixedTooltipPortal from 'dpl/common/tooltip/components/FixedTooltipPortal';
import GenericMenu from 'dpl/path_to_payment/components/GenericMenu';
import VennImages from 'dpl/components/VennImages';

const IS_LT_MD = isLessThanBreakpoint('md');

/** @param {any} props */
function LitterOptionDetails({
  className = null,
  litter = null,
  isOtherTbdLitter = false,
  tbdDescription = 'I’ll assign the litter later'
}) {
  if (!litter) {
    const litterName = isOtherTbdLitter ? 'Other' : 'TBD x TBD';

    const description = isOtherTbdLitter
      ? 'Add description instead'
      : tbdDescription;

    return (
      <span className={classnames('flex flex-column flex-auto', className)}>
        <span className="sr-only">Litter</span>
        <span className="truncate font-16 lh-body">{litterName}</span>
        <span className="tertiary font-12">{description}</span>
      </span>
    );
  }

  return (
    <span className={classnames('flex flex-column flex-auto', className)}>
      <span className="sr-only">Litter</span>
      <span className="truncate font-16 lh-body">{litter.name}</span>
      <span className="tertiary font-12">
        {litter.status && (
          <>
            <span className="sr-only">with a litter status of</span>
            <span className="ttc dib">{litter.status}</span>
          </>
        )}
        {litter.birth_date && (
          <>
            <span className="sr-only">with a litter birth date of</span>
            <span> {litter.birth_date}</span>
          </>
        )}
        {(litter.status || litter.birth_date) &&
          litter.total_puppy_count > 0 &&
          ' • '}
        {litter.total_puppy_count > 0 && (
          <>
            <span className="sr-only">with</span>
            <span>
              {litter.reserved_puppies_count}/{litter.total_puppy_count} dogs
              reserved
            </span>
          </>
        )}
      </span>
    </span>
  );
}

LitterOptionDetails.propTypes = {
  className: PropTypes.string,
  isOtherTbdLitter: PropTypes.bool,
  tbdDescription: PropTypes.string,
  litter: PropTypes.shape({
    birth_date: PropTypes.string,
    name: PropTypes.string.isRequired,
    reserved_puppies_count: PropTypes.number,
    status: PropTypes.string.isRequired,
    total_puppy_count: PropTypes.number
  })
};

/** @param {any} props */
function LitterAssignmentDropdownButton({
  className = null,
  handleLitterClick,
  isTbdLitter = false,
  isOtherTbdLitter = false,
  tbdDescription = undefined,
  litter = null
}) {
  const id = useMemo(() => {
    if (isTbdLitter && isOtherTbdLitter) return 'tbd_other';
    if (isTbdLitter) return 'tbd';
    return `${litter.id}`;
  }, [isTbdLitter, isOtherTbdLitter, litter]);

  function handleClick() {
    if (isTbdLitter) {
      handleLitterClick(null, { isOtherTbdLitter });
    } else {
      handleLitterClick(litter.id);
    }
  }

  return (
    <button
      id={`litter_option_${id}`}
      data-test-id={`litter_option_${id}`}
      type="button"
      className={classnames('tl w-100', className)}
      onClick={handleClick}
    >
      <LitterOptionDetails
        litter={isTbdLitter ? null : litter}
        tbdDescription={tbdDescription}
        isOtherTbdLitter={isOtherTbdLitter}
      />
    </button>
  );
}

LitterAssignmentDropdownButton.propTypes = {
  className: PropTypes.string,
  handleLitterClick: PropTypes.func.isRequired,
  isTbdLitter: PropTypes.bool,
  isOtherTbdLitter: PropTypes.bool,
  tbdDescription: PropTypes.string,
  litter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  })
};

/** @param {any} props */
export function LitterSelector({
  availableLitters,
  selectedLitterId = null,
  onLitterChange,
  disableTbd = false,
  enableOtherTbd = false,
  tbdDescription = undefined,
  newDesignSystemStyles = false,
  isOtherTbdLitter = false
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const selectedLitter = availableLitters.find(
    litter => litter.id === selectedLitterId
  );

  function renderEmptyLitterOption() {
    if (disableTbd) {
      return <span className="disabled w-100">Select litter</span>;
    }

    return (
      <div className="flex items-center flex-auto">
        <VennImages
          width="26px"
          height="26px"
          className="dib mr2 flex-none"
          images={[{ alt: 'tbd_sire' }, { alt: 'tbd_dam' }]}
          isBordered
          isOverlapping
          isSmall
        />
        <LitterOptionDetails
          className={classnames({ 'fw-medium': newDesignSystemStyles })}
          tbdDescription={tbdDescription}
          isOtherTbdLitter={isOtherTbdLitter}
        />
      </div>
    );
  }

  return (
    <div className="LitterAssignmentDropdown">
      <Label
        htmlFor="litter-assignment-dropdown"
        newDesignSystemStyles
        text="Litter"
      />
      <FancyDropdown
        id="litter-assignment-dropdown"
        onChange={setIsDropdownOpen}
        isOpen={isDropdownOpen}
        maxMenuHeight="200px"
      >
        <FancyDropdownToggle
          className="bg-white ba br3 b--default pv3 ph3 font-16"
          id="FancyDropdownToggle_LitterAssignmentDropdown"
        >
          <div className="flex items-center">
            {selectedLitter?.id ? (
              <div className="flex items-center flex-auto">
                <LitterParentsVennImages
                  litterParents={selectedLitter.parents}
                  vennImageProps={{
                    width: '26px',
                    height: '26px',
                    className: 'dib mr2 flex-none',
                    isBordered: true,
                    isOverlapping: true
                  }}
                />
                <LitterOptionDetails litter={selectedLitter} />
              </div>
            ) : (
              renderEmptyLitterOption()
            )}
            <Icon
              className="secondary"
              name={`fetch-chevron-${isDropdownOpen ? 'up' : 'down'}`}
            />
          </div>
        </FancyDropdownToggle>
        {IS_LT_MD ? (
          <FixedTooltipPortal
            isVisible={isDropdownOpen}
            onCloseClick={() => setIsDropdownOpen(false)}
            style={{ maxHeight: '90vh' }}
            shouldHideCloseButton
            className="no-scrollbars"
            wrapperClassName="dn-ns"
          >
            <GenericMenu setIsMenuOpen={setIsDropdownOpen} className="ph1">
              {availableLitters.map((litter, idx) => (
                <LitterAssignmentDropdownButton
                  key={idx}
                  className={classnames('pv6 bt b--subtle-gray', {
                    'bt-0': idx === 0,
                    'fw-medium': newDesignSystemStyles
                  })}
                  litter={litter}
                  handleLitterClick={onLitterChange}
                />
              ))}
              {!disableTbd && (
                <LitterAssignmentDropdownButton
                  className={classnames('pv6 bt b--subtle-gray', {
                    'fw-medium': newDesignSystemStyles
                  })}
                  handleLitterClick={onLitterChange}
                  tbdDescription={tbdDescription}
                  isTbdLitter
                />
              )}
              {!disableTbd && enableOtherTbd && (
                <LitterAssignmentDropdownButton
                  className="pv6 bt b--subtle-gray"
                  handleLitterClick={onLitterChange}
                  isTbdLitter
                  isOtherTbdLitter
                />
              )}
            </GenericMenu>
          </FixedTooltipPortal>
        ) : (
          <FancyDropdownMenu
            className={classnames(
              'LitterAssignmentDropdown__menu flex-auto mt1',
              {
                'br3 box-shadow-2 bg-white overflow-y-auto':
                  !newDesignSystemStyles
              }
            )}
            newDesignSystemStyles={newDesignSystemStyles}
          >
            {availableLitters.map(litter => (
              <FancyDropdownMenuItem
                key={litter.id}
                itemKey={litter.id}
                className="br3 bg-white pointer"
              >
                <LitterAssignmentDropdownButton
                  className="br2 ph2 pv2 hover-bg-near-white"
                  litter={litter}
                  handleLitterClick={onLitterChange}
                />
              </FancyDropdownMenuItem>
            ))}
            {!disableTbd && (
              <FancyDropdownMenuItem
                key="tbd-litter"
                itemKey="tbd-litter"
                className="br3 bg-white pointer"
              >
                <LitterAssignmentDropdownButton
                  className="br2 ph2 pv2 hover-bg-near-white"
                  handleLitterClick={onLitterChange}
                  tbdDescription={tbdDescription}
                  isTbdLitter
                />
              </FancyDropdownMenuItem>
            )}
            {!disableTbd && enableOtherTbd && (
              <FancyDropdownMenuItem
                key="other-tbd-litter"
                itemKey="other-tbd-litter"
                className="br3 bg-white pointer"
              >
                <LitterAssignmentDropdownButton
                  className="br2 ph2 pv2 hover-bg-near-white"
                  handleLitterClick={onLitterChange}
                  isTbdLitter
                  isOtherTbdLitter
                />
              </FancyDropdownMenuItem>
            )}
          </FancyDropdownMenu>
        )}
      </FancyDropdown>
    </div>
  );
}

LitterSelector.propTypes = {
  tbdDescription: PropTypes.string,
  availableLitters: PropTypes.arrayOf(
    PropTypes.shape({
      parents: PropTypes.arrayOf(
        PropTypes.shape({
          profile_photo_url: PropTypes.string
        })
      ),
      sire_name: PropTypes.string,
      dam_name: PropTypes.string,
      id: PropTypes.number.isRequired
    })
  ).isRequired,
  onLitterChange: PropTypes.func.isRequired,
  selectedLitterId: PropTypes.number,
  disableTbd: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool,
  enableOtherTbd: PropTypes.bool,
  isOtherTbdLitter: PropTypes.bool
};
/** @param {any} props */
export default function LitterAssignmentDropdown({
  availableLitters,
  buyerProfileId,
  onLitterChange,
  selectedLitterId = null,
  isTbdLitter = false,
  newDesignSystemStyles = false,
  isOtherTbdLitter = false,
  enableOtherTbd = false,
  autoselectDefault = true
}) {
  const { assignedLitter, isLoadingData } =
    useAssignableLitterData(buyerProfileId);

  const defaultSelectedLitter =
    (assignedLitter?.id && assignedLitter) || availableLitters[0];

  useEffect(() => {
    if (
      autoselectDefault &&
      !isLoadingData &&
      !selectedLitterId &&
      !isTbdLitter
    ) {
      onLitterChange(defaultSelectedLitter?.id);
    }
  }, [autoselectDefault, defaultSelectedLitter?.id, isLoadingData]);

  if (availableLitters.length === 0) {
    return null;
  }

  return (
    <LitterSelector
      availableLitters={availableLitters}
      selectedLitterId={selectedLitterId}
      onLitterChange={onLitterChange}
      newDesignSystemStyles={newDesignSystemStyles}
      enableOtherTbd={enableOtherTbd}
      isOtherTbdLitter={isOtherTbdLitter}
    />
  );
}

LitterAssignmentDropdown.propTypes = {
  availableLitters: PropTypes.arrayOf(
    PropTypes.shape({
      parents: PropTypes.arrayOf(
        PropTypes.shape({
          profile_photo_url: PropTypes.string
        })
      ),
      sire_name: PropTypes.string,
      dam_name: PropTypes.string,
      id: PropTypes.number.isRequired
    })
  ).isRequired,
  buyerProfileId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  onLitterChange: PropTypes.func.isRequired,
  selectedLitterId: PropTypes.number,
  isTbdLitter: PropTypes.bool,
  newDesignSystemStyles: PropTypes.bool,
  isOtherTbdLitter: PropTypes.bool,
  autoselectDefault: PropTypes.bool,
  enableOtherTbd: PropTypes.bool
};
